import { SampleDetailsType } from "src/common/types/sample";

export type SampleDetailField = {
  label: string;
  value: string | null | undefined;
};

export type SampleDetailsSection = {
  title: string;
  fields: SampleDetailField[];
};

export const getSampleDetailsSections = (
  data: Partial<SampleDetailsType> | null,
  extended: boolean = false
): SampleDetailsSection[] => {
  if (!data) return [];

  const baseSections: SampleDetailsSection[] = [
    {
      title: 'Sample information',
      fields: [
        { label: 'Sample name', value: data['sample original name'] },
        { label: 'Alternate name', value: data['alternate name'] },
        { label: 'Section name', value: data['section name'] },
        { label: 'Height/depth (m)', value: data['height/depth'] },
        {
          label: 'State, Country',
          value: [data['state/province'], data['country']]
            .filter((x) => !!x)
            .join(', '),
        },
        { label: 'Site description', value: data['site description'] },
        { label: 'Latitude', value: data['site latitude'] },
        { label: 'Longitude', value: data['site longitude'] },
        { label: 'Sample notes', value: data['sample notes'] },
      ],
    },
    {
      title: 'Geological context',
      fields: [
        { label: 'Craton/terrane', value: data['craton/terrane'] },
        { label: 'Basin name', value: data['basin name'] },
        { label: 'Basin type', value: data['basin type'] },
        { label: 'Metamorphic bin', value: data['metamorphic bin'] },
        { label: 'Environmental bin', value: data['environmental bin'] },
        { label: 'Stratigraphic unit', value: data['stratigraphic unit'] },
        { label: 'Geological Age', value: data['geological age'] },
        { label: 'Interpreted age (Ma)', value: data['interpreted age'] },
        { label: 'Minimum Age', value: data['min age'] },
        { label: 'Maximum Age', value: data['max age'] },
        {
          label: 'Interpreted age notes',
          value: data['interpreted age justification'],
        },
        {
          label: 'Lithology',
          value: [
            data['color shade'],
            data['color'],
            data['lithology texture'],
            data['lithology composition'],
            data['lithology name'],
          ]
            .filter((x) => !!x)
            .join(' '),
        },
        { label: 'Lithology notes', value: data['lithology notes'] },
        {
          label: 'Verbatim stratigraphic unit',
          value: data['verbatim stratigraphic unit'],
        },
      ],
    },
    {
      title: 'Collection information',
      fields: [
        { label: 'Project name', value: data['project name'] },
        { label: 'Collected by', value: data['collector name'] },
        {
          label: 'Geological context provided by',
          value: data.geol_context_provided_by,
        },
        {
          label: 'Interpreted age provided by',
          value: data.interpreted_age_provided_by,
        },
      ],
    },
  ];

  if (extended) {
    const geologicalContextIndex = baseSections.findIndex(
      (s) => s.title === 'Geological context'
    );
    if (geologicalContextIndex !== -1) {
      baseSections[geologicalContextIndex].fields.push(
        { label: 'Verbatim fossil(s)', value: data['verbatim fossil(s)'] },
        {
          label: 'Sedimentary structure(s)',
          value: data['sedimentary structure(s)'],
        }
      );
    }

    const collectionIndex = baseSections.findIndex(
      (s) => s.title === 'Collection information'
    );
    if (collectionIndex !== -1) {
      baseSections[collectionIndex].fields.push(
        { label: 'Data Source', value: data['data source'] },
        {
          label: 'Earliest Collection Date',
          value: data['earliest collection date'],
        },
        {
          label: 'Latest Collection Date',
          value: data['latest collection date'],
        }
      );
    }
  }

  return baseSections;
};
